import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AdminAutomatedEmailListItem } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminAutomatedEmailsQuery } from '@/hooks/api'
import { CellLink } from '@/utils/data-table'
import { formatDateTime } from '@/utils/format'
import { EMAIL_TYPE_LABELS, type EmailType, getEmailTypeLabel } from './email-type-labels'

type SearchParams = {
  page?: number
  search?: string
  emailType?: EmailType
}

export const Route = createFileRoute('/_protected/admin/emails/')({
  component: AdminEmailsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
    emailType: (search.emailType as EmailType) || undefined,
  }),
})

const LIMIT = 20

const columns: TableColumn<AdminAutomatedEmailListItem>[] = [
  {
    header: 'ID',
    width: '100px',
    render: (email) => (
      <CellLink to="/admin/emails/$id" params={{ id: email.id }} className="font-mono text-xs">
        {email.id.slice(0, 8)}
      </CellLink>
    ),
  },
  {
    header: 'Type',
    render: (email) => (
      <Badge variant="secondary" size="sm">
        {getEmailTypeLabel(email.emailType)}
      </Badge>
    ),
  },
  {
    header: 'Recipient',
    render: (email) => (
      <CellLink to="/admin/users/$id" params={{ id: email.user.id }}>
        {email.recipientEmail}
      </CellLink>
    ),
  },
  {
    header: 'Sent',
    width: '200px',
    render: (email) => <CellText>{formatDateTime(email.createdAt)}</CellText>,
  },
]

function AdminEmailsPage() {
  const { page = 1, search, emailType } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const emailsQuery = useAdminAutomatedEmailsQuery({
    query: {
      page,
      limit: LIMIT,
      search: search || undefined,
      emailType,
    },
  })

  const emails = emailsQuery.data?.data?.items ?? []
  const total = emailsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-6 py-6">
      <div className="flex items-center gap-3">
        <SearchInput
          placeholder="Search by email or name..."
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() =>
            navigate({ search: { page: 1, search: searchInput || undefined, emailType } })
          }
        />
        <select
          value={emailType ?? ''}
          onChange={(e) =>
            navigate({
              search: {
                page: 1,
                search,
                emailType: (e.target.value as EmailType) || undefined,
              },
            })
          }
          className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground"
        >
          <option value="">All types</option>
          {Object.entries(EMAIL_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <DataTable
        columns={columns}
        data={emails}
        isLoading={emailsQuery.isLoading}
        emptyMessage="No automated emails found."
        keyExtractor={(e) => e.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search, emailType } })}
      />
    </div>
  )
}
